import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Box, Typography, Form, Loader, useWatch } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import { useStore } from '../../../../../store/store';
import { useUserIdentity } from '@lucid/portal-core';
import type { Prospect } from '../../../../../api/services/lupp/prospect/dto.prospect';
import type {
  ProspectTestDrive,
  CreateProspectTestDriveRequest,
  TestDriveStatus,
} from '../../../../../api/services/lupp/prospect/dto.test-drive-schedule';
import type { Theme } from '../utils';
import {
  useGetSalesConsultantsCalendar,
  useGetTestDriveAvailableSlots,
  useGetTestDriveVehicles,
} from '../../../../../api/services/lupp/test-drive/test-drive';
import {
  useCreateProspectTestDrive,
  useUpdateProspectTestDrive,
} from '../../../../../api/services/lupp/prospect/test-drive-schedule';
import {
  localToUTC,
  zonedToUTC,
  utcToLocalDate,
  utcToZonedTimeSlot,
  toZuluDatetime,
  formatToOffsetDateTime,
} from '../../../../../utils/tools';
import {
  isExtendedTestDriveEnabled,
  isTestDriveHostAssignmentEnabled,
} from '../../../../../config/featureFlags';
import { LOCKED_TEST_DRIVE_STATUSES } from '../../../constants';
import {
  createScheduleTestDriveFormFields,
  DEFAULT_HANDOVER_MINUTES,
  findWindowCombination,
  getExtendedPickupOptions,
  getExtendedReturnOptions,
  getOfferedTimeOptions,
  getSavedTimeSlot,
  getWindowTimes,
  isExtendedTestDrive,
  TEST_DRIVE_TYPE_EXTENDED,
  TEST_DRIVE_TYPE_STANDARD,
} from './formFields';
import { getHostUnavailableMessage, getSalesConsultantOptions } from './utils';
import { useTestDriveCheckIn } from './useTestDriveCheckIn';
import CheckInAction from './CheckInAction';
import styles from './TestDriveTab.module.scss';

type ScheduleTestDriveFormProps = {
  prospect?: Prospect;
  /** Undefined means booking a brand-new test drive; set means modifying this one. */
  testDriveSchedule?: ProspectTestDrive;
  onBack: () => void;
  onSaved: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  labels: Record<string, string>;
  theme: Theme;
  /** When modifying, lets the rep jump straight back to the test drive list. */
  onBackToList?: () => void;
};

const normalizeScheduleDate = (value?: string): string => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const dotted = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dotted) return `${dotted[3]}-${dotted[2]}-${dotted[1]}`;
  const slashed = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashed) return `${slashed[3]}-${slashed[1]}-${slashed[2]}`;
  return value;
};

const ScheduleTestDriveForm: React.FC<ScheduleTestDriveFormProps> = ({
  prospect,
  testDriveSchedule,
  onBack,
  onSaved,
  onDirtyChange,
  labels,
  theme,
  onBackToList,
}) => {
  const { userIdentity } = useUserIdentity();
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const isEditing = Boolean(testDriveSchedule?.testDriveScheduleId);
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';
  // Last resort so a prospect missing prospectOwnerLocation still yields a non-blank value;
  // the API interceptor turns '' into null, which x-sales's ProspectTestDriveRequest rejects.
  const partnerOrgLocation = userIdentity?.businessPartnerOrgLocation;
  // The prospect's location, which prospects also books the test drive against. Vehicles, slots
  // and consultants are read for it so they aren't merged across the agency's other locations.
  const locationId = prospect?.businessPartnerOrganizationLocationIdentifier;

  const { mutate: createTestDrive, isPending: isCreating } = useCreateProspectTestDrive();
  const { mutate: updateTestDrive, isPending: isUpdating } = useUpdateProspectTestDrive();
  const isPending = isCreating || isUpdating;

  const { data: testDriveVehicles } = useGetTestDriveVehicles({
    agencyId: userIdentity?.businessOrganizationId ?? '',
    locationId,
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const formRef = useRef<FormRef>(null);

  const checkIn = useTestDriveCheckIn({
    prospect,
    testDrive: testDriveSchedule,
    labels,
    onError: setErrorMessage,
  });

  const selectedModelTrim = useWatch<string>(formRef, 'selectedModelTrim');
  const scheduledDate = useWatch<string>(formRef, 'scheduledDate');
  const watchedTestDriveType = useWatch<string>(formRef, 'testDriveType');
  const returnDate = useWatch<string>(formRef, 'returnDate');
  const returnTime = useWatch<string>(formRef, 'returnTime');
  const savedTestDriveType = isExtendedTestDrive(testDriveSchedule?.testDriveType)
    ? TEST_DRIVE_TYPE_EXTENDED
    : TEST_DRIVE_TYPE_STANDARD;
  const testDriveType = watchedTestDriveType || savedTestDriveType;
  const isExtended = isExtendedTestDrive(testDriveType);

  const hasRestoredModelRef = useRef(false);

  const selectedVehicle = testDriveVehicles?.find(
    (vehicle) => vehicle.description === selectedModelTrim
  );

  const savedVehicle = testDriveVehicles?.find(
    (vehicle) =>
      vehicle.model === testDriveSchedule?.model && vehicle.trim === testDriveSchedule?.trim
  );

  useEffect(() => {
    if (!isEditing || hasRestoredModelRef.current || !savedVehicle) return;
    const isValidSelection = testDriveVehicles?.some(
      (vehicle) => vehicle.description === selectedModelTrim
    );
    if (!selectedModelTrim || !isValidSelection) {
      formRef.current?.setValue('selectedModelTrim', savedVehicle.description, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
    hasRestoredModelRef.current = true;
  }, [isEditing, savedVehicle, selectedModelTrim, testDriveVehicles]);

  const { data: availableSlots } = useGetTestDriveAvailableSlots({
    agencyId: userIdentity?.businessOrganizationId ?? '',
    locationId,
    model: selectedVehicle?.model ?? '',
    trim: selectedVehicle?.trim,
    selectedDate: normalizeScheduleDate(scheduledDate),
    testDriveType: isExtended ? TEST_DRIVE_TYPE_EXTENDED : TEST_DRIVE_TYPE_STANDARD,
    returnDate: isExtended ? normalizeScheduleDate(returnDate) : undefined,
    // Keeps this booking's own current slot selectable while modifying it.
    excludeBookingId: testDriveSchedule?.testDriveScheduleId,
    enabled: !isExtended || Boolean(returnDate),
  });

  // A booking keeps its own timezone even when its date offers no slots.
  const agencyTimeZone = availableSlots?.slotDetails?.[0]?.timezone ?? testDriveSchedule?.timezone;

  const selectedStatus = useWatch<string>(formRef, 'status');
  // Cancelling or marking a no-show keeps the booked slot, even once it's no longer offered.
  const isClosingBooking =
    isEditing && LOCKED_TEST_DRIVE_STATUSES.includes(selectedStatus?.toLowerCase() ?? '');

  // The form keeps its initial time even when the slots stop offering it, which would
  // show an empty dropdown yet still save that time. Clear it once the slots have loaded.
  const scheduledTime = useWatch<string>(formRef, 'scheduledTime');
  const windowCombinations = availableSlots?.availableWindowCombinations;
  const handoverMinutes = availableSlots?.handoverDurationMinutes ?? DEFAULT_HANDOVER_MINUTES;
  const pickupOptions = useMemo(
    () =>
      isExtended
        ? getExtendedPickupOptions(windowCombinations, handoverMinutes)
        : getOfferedTimeOptions(availableSlots?.slotDetails),
    [isExtended, windowCombinations, handoverMinutes, availableSlots?.slotDetails]
  );
  const returnOptions = useMemo(
    () => getExtendedReturnOptions(windowCombinations, handoverMinutes),
    [windowCombinations, handoverMinutes]
  );
  useEffect(() => {
    if (isClosingBooking || !availableSlots || !scheduledTime) return;
    const isOffered = pickupOptions.some((option) => option.value === scheduledTime);
    if (!isOffered) {
      formRef.current?.setValue('scheduledTime', '', { shouldValidate: false, shouldDirty: false });
    }
  }, [isClosingBooking, availableSlots, scheduledTime, pickupOptions]);

  // Same stale-time guard for the return time.
  useEffect(() => {
    if (!isExtended || isClosingBooking || !availableSlots || !returnTime) return;
    const isOffered = returnOptions.some((option) => option.value === returnTime);
    if (!isOffered) {
      formRef.current?.setValue('returnTime', '', { shouldValidate: false, shouldDirty: false });
    }
  }, [isExtended, isClosingBooking, availableSlots, returnTime, returnOptions]);

  // A saved extended booking stores the pickup and return instants, so its window is matched
  // back to the dropdowns by those once the combinations have loaded.
  const hasRestoredExtendedTimesRef = useRef(false);
  useEffect(() => {
    if (
      !isEditing ||
      !isExtendedTestDrive(testDriveSchedule?.testDriveType) ||
      hasRestoredExtendedTimesRef.current ||
      !windowCombinations
    )
      return;
    const { scheduledStartDatetime, scheduledEndDatetime } = testDriveSchedule ?? {};
    const savedWindow = windowCombinations.find(
      (combination) =>
        Date.parse(combination.pickup_datetime_utc) === Date.parse(scheduledStartDatetime ?? '') &&
        Date.parse(combination.drop_datetime_utc) === Date.parse(scheduledEndDatetime ?? '')
    );
    if (savedWindow) {
      const { pickupTime, returnTime: savedReturnTime } = getWindowTimes(savedWindow);
      const options = { shouldValidate: false, shouldDirty: false };
      formRef.current?.setValue('scheduledTime', pickupTime, options);
      formRef.current?.setValue('returnTime', savedReturnTime, options);
    }
    hasRestoredExtendedTimesRef.current = true;
  }, [isEditing, testDriveSchedule, windowCombinations]);

  const findSlot = (time?: string) =>
    availableSlots?.slotDetails?.find(
      (slot) => utcToZonedTimeSlot(slot.startTime, slot.endTime, slot.timezone) === time
    );
  const watchedSlot = findSlot(scheduledTime);

  const isHostFlagOn = isTestDriveHostAssignmentEnabled();
  const isExtendedFlagOn = isExtendedTestDriveEnabled();
  // Extended test drives have no host field.
  const isHostAssignmentEnabled = isHostFlagOn && !isExtended;
  // The prefilled host isn't saved yet, so the untouched form still has a change to submit.
  const isHostUnsaved = isHostAssignmentEnabled && !testDriveSchedule?.hostUserIdentifier;
  const selectedHost = useWatch<string>(formRef, 'hostUserIdentifier');

  const { data: salesConsultantsCalendar } = useGetSalesConsultantsCalendar({
    agencyId: userIdentity?.businessOrganizationId ?? '',
    locationId,
    date: normalizeScheduleDate(scheduledDate),
    enabled: isHostAssignmentEnabled,
  });

  const salesConsultants = useMemo(() => {
    const { scheduledStartDatetime, scheduledEndDatetime } = testDriveSchedule ?? {};
    const ownBooking =
      isEditing && prospect?.prospectIdentifier && scheduledStartDatetime && scheduledEndDatetime
        ? {
            prospectId: prospect.prospectIdentifier,
            start: scheduledStartDatetime,
            end: scheduledEndDatetime,
          }
        : undefined;
    const slot = watchedSlot
      ? { start: watchedSlot.startTime, end: watchedSlot.endTime }
      : undefined;
    return getSalesConsultantOptions(salesConsultantsCalendar, slot, ownBooking);
  }, [salesConsultantsCalendar, watchedSlot, testDriveSchedule, isEditing, prospect]);

  const selfId = userIdentity?.userPortalId;
  const isSelfAvailable = salesConsultants.some(
    (consultant) => consultant.value === selfId && consultant.isAvailable
  );

  useEffect(() => {
    if (
      !isHostAssignmentEnabled ||
      isClosingBooking ||
      testDriveSchedule?.hostUserIdentifier ||
      selectedHost ||
      !selfId ||
      !isSelfAvailable
    )
      return;
    formRef.current?.setValue('hostUserIdentifier', selfId, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [
    isHostAssignmentEnabled,
    isClosingBooking,
    testDriveSchedule?.hostUserIdentifier,
    selectedHost,
    selfId,
    isSelfAvailable,
  ]);

  // A host picked for one time may be busy at the next, so it's picked again once a time is.
  useEffect(() => {
    if (!isHostAssignmentEnabled || isClosingBooking || scheduledTime || !selectedHost) return;
    formRef.current?.setValue('hostUserIdentifier', '', {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [isHostAssignmentEnabled, isClosingBooking, scheduledTime, selectedHost]);

  // Puts back the booked slot and host, since they may have been cleared or edited before.
  useEffect(() => {
    if (!isClosingBooking || !testDriveSchedule) return;
    const options = { shouldValidate: false, shouldDirty: false };
    if (savedVehicle) {
      formRef.current?.setValue('selectedModelTrim', savedVehicle.description, options);
    }
    formRef.current?.setValue(
      'scheduledDate',
      utcToLocalDate(testDriveSchedule.scheduledStartDatetime, locale),
      options
    );
    const savedSlot = getSavedTimeSlot(testDriveSchedule, agencyTimeZone, locale);
    // Extended bookings have no single saved slot, so their pickup time is left as it is.
    if (savedSlot || !isExtendedTestDrive(testDriveSchedule.testDriveType)) {
      formRef.current?.setValue('scheduledTime', savedSlot, options);
    }
    if (isExtendedTestDrive(testDriveSchedule.testDriveType)) {
      formRef.current?.setValue(
        'returnDate',
        utcToLocalDate(testDriveSchedule.scheduledEndDatetime, locale),
        options
      );
    }
    if (isHostAssignmentEnabled) {
      formRef.current?.setValue(
        'hostUserIdentifier',
        testDriveSchedule.hostUserIdentifier ?? '',
        options
      );
    }
  }, [
    isClosingBooking,
    testDriveSchedule,
    savedVehicle,
    locale,
    agencyTimeZone,
    isHostAssignmentEnabled,
  ]);

  const formFields = useMemo(
    () =>
      createScheduleTestDriveFormFields({
        prospect,
        onCancel: onBack,
        testDriveSchedule,
        organizationName: userIdentity?.organizationName,
        labels,
        locale,
        partnerOrgLocation: prospect?.prospectOwnerLocation || partnerOrgLocation,
        testDriveVehicles,
        availableSlots: availableSlots?.slotDetails,
        availableWindowCombinations: windowCombinations,
        handoverMinutes,
        isExtendedEnabled: isExtendedFlagOn,
        availability: {
          isAvailable: availableSlots?.isAvailable,
          status: availableSlots?.availabilityStatus,
        },
        selection: { modelTrim: selectedModelTrim, date: normalizeScheduleDate(scheduledDate) },
        testDriveType,
        returnDate: normalizeScheduleDate(returnDate),
        agencyTimeZone,
        theme: invertedTheme,
        onBackToList,
        salesConsultant: isHostAssignmentEnabled
          ? {
              consultants: salesConsultants,
              defaultValue: testDriveSchedule?.hostUserIdentifier,
              selectedHost,
              hasSlot: Boolean(watchedSlot),
            }
          : undefined,
        hideRequestedStatus: isHostFlagOn,
        isClosingBooking,
      }),
    [
      prospect,
      onBack,
      testDriveSchedule,
      userIdentity?.organizationName,
      labels,
      locale,
      testDriveVehicles,
      availableSlots,
      windowCombinations,
      handoverMinutes,
      selectedModelTrim,
      scheduledDate,
      testDriveType,
      returnDate,
      agencyTimeZone,
      invertedTheme,
      onBackToList,
      partnerOrgLocation,
      isHostAssignmentEnabled,
      isHostFlagOn,
      isExtendedFlagOn,
      salesConsultants,
      selectedHost,
      watchedSlot,
      isClosingBooking,
    ]
  );

  const buildDatetime = (date: string, time: string, now: string): string => {
    if (!date || !time) return now;
    try {
      let isoDate = normalizeScheduleDate(date);
      if (date.includes('/')) {
        const parts = date.split('/');
        if (parts.length === 3) {
          const [month, day, year] = parts;
          isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
      const [hours, minutes] = time.split(':').map(Number);
      return agencyTimeZone
        ? zonedToUTC(isoDate, hours, minutes, agencyTimeZone)
        : localToUTC(isoDate, hours, minutes, locale);
    } catch {
      return now;
    }
  };

  const handleSuccess = (data: unknown) => {
    if (!prospect?.prospectIdentifier) {
      setErrorMessage(
        labels?.['details.prospectIdMissing'] ?? 'Prospect ID is missing. Cannot save.'
      );
      return;
    }
    setErrorMessage(null);

    const formData = data as {
      scheduledDate?: string;
      scheduledTime?: string;
      returnTime?: string;
      selectedModelTrim?: string;
      status?: string;
      hostUserIdentifier?: string;
    };

    const now = new Date().toISOString();
    const timeSlot = formData.scheduledTime || '';
    const [startTime, endTime] = timeSlot.split('-').map((t) => t?.trim() || '');
    const scheduledDateValue = formData.scheduledDate || '';

    // An extended drive runs from the chosen window's pickup to its drop.
    const chosenWindow = isExtended
      ? findWindowCombination(windowCombinations, formData.scheduledTime, formData.returnTime)
      : undefined;
    if (isExtended && !isClosingBooking && !chosenWindow) {
      setErrorMessage(
        labels?.['form.extendedWindowUnavailable'] ??
          'No vehicle is available for the selected pickup and return time. Please choose another time.'
      );
      return;
    }
    const startDatetime = chosenWindow
      ? formatToOffsetDateTime(new Date(chosenWindow.pickup_datetime_utc))
      : buildDatetime(scheduledDateValue, startTime, now);
    const endDatetime = chosenWindow
      ? formatToOffsetDateTime(new Date(chosenWindow.drop_datetime_utc))
      : buildDatetime(scheduledDateValue, endTime, now);
    if (chosenWindow && Date.parse(endDatetime) <= Date.parse(startDatetime)) {
      setErrorMessage(
        labels?.['form.returnBeforePickup'] ?? 'Return must be after the pickup date and time.'
      );
      return;
    }

    const selectedSlot = findSlot(formData.scheduledTime);
    const slotVehicle =
      selectedSlot?.tdEventConfig?.find(
        (config) => config.model === selectedVehicle?.model && config.trim === selectedVehicle?.trim
      ) ?? selectedSlot?.tdEventConfig?.[0];
    // The vehicle and sales consultant the API picked for this window.
    const windowVehicle = chosenWindow?.recommended_vehicle;
    const windowHost = chosenWindow?.recommended_pickup_sales_agent?.sales_consultant_identifier;
    // Extended has no host field, so it takes the pickup consultant the API picked.
    const resolveHost = () => {
      if (isHostAssignmentEnabled) return formData.hostUserIdentifier;
      return isHostFlagOn && isExtended ? windowHost : undefined;
    };

    // Ownership (businessPartnerUserId/OrganizationId) and lifecycle flags (isActive/isDeleted)
    // are server-controlled by ProspectTestDriveRequest's contract, not client-editable — only
    // the fields it actually declares are built here (see test-drive-schedule.ts's toApiRequestPayload).
    const payload: CreateProspectTestDriveRequest = {
      ...testDriveSchedule,
      testDriveStatus: (formData.status as TestDriveStatus) || 'scheduled',
      testDriveType: isExtended ? TEST_DRIVE_TYPE_EXTENDED : TEST_DRIVE_TYPE_STANDARD,
      scheduledStartDatetime: isClosingBooking
        ? testDriveSchedule?.scheduledStartDatetime
        : toZuluDatetime(startDatetime),
      scheduledEndDatetime: isClosingBooking
        ? testDriveSchedule?.scheduledEndDatetime
        : toZuluDatetime(endDatetime),
      appointmentLocationName:
        testDriveSchedule?.appointmentLocationName ||
        prospect.prospectOwnerLocation ||
        partnerOrgLocation ||
        '',
      eventConfigId:
        slotVehicle?.eventConfigId ??
        windowVehicle?.test_drive_event_configuration_identifier ??
        testDriveSchedule?.eventConfigId,
      vin: slotVehicle?.vin ?? windowVehicle?.vin ?? testDriveSchedule?.vin,
      make: slotVehicle?.make ?? selectedVehicle?.make ?? testDriveSchedule?.make,
      trim: selectedVehicle?.trim ?? formData.selectedModelTrim ?? testDriveSchedule?.trim,
      model: selectedVehicle?.model ?? testDriveSchedule?.model,
      timezone:
        selectedSlot?.timezone ??
        agencyTimeZone ??
        availableSlots?.locationTimezone ??
        testDriveSchedule?.timezone ??
        'GMT',
      // Left off entirely when the flag is off so saving never touches the host.
      hostUserIdentifier: resolveHost(),
    };

    const onMutationSuccess = () => {
      onDirtyChange?.(false);
      onSaved();
    };
    const onMutationError = (error: unknown) => {
      const errorCode = (error as { responseData?: { code?: string } } | null)?.responseData?.code;
      if (errorCode === 'HOST_UNAVAILABLE') {
        setErrorMessage(getHostUnavailableMessage(labels));
        return;
      }
      const errorMsg =
        error instanceof Error && error.message
          ? error.message
          : (labels?.['testDrive.saveError'] ?? 'Failed to save the test drive. Please try again.');
      setErrorMessage(errorMsg);
    };

    if (isEditing && testDriveSchedule?.testDriveScheduleId) {
      updateTestDrive(
        {
          prospectId: prospect.prospectIdentifier,
          testDriveId: testDriveSchedule.testDriveScheduleId,
          data: payload,
        },
        { onSuccess: onMutationSuccess, onError: onMutationError }
      );
    } else {
      createTestDrive(
        { prospectId: prospect.prospectIdentifier, data: payload },
        { onSuccess: onMutationSuccess, onError: onMutationError }
      );
    }
  };

  const handleError = (error: unknown) => {
    console.error('Test drive form validation failed:', error);
    setErrorMessage(
      labels?.['details.validationError'] ?? 'Please fix the form errors before submitting.'
    );
  };

  return (
    <Box>
      {errorMessage && (
        <Box className={styles.errorMessageText}>
          <Typography variant="body" size="medium" color="error-1">
            {errorMessage}
          </Typography>
        </Box>
      )}
      {isPending && (
        <Box className={styles.loaderContainer}>
          <Loader />
        </Box>
      )}
      {isEditing && (
        <Box pb={16}>
          <CheckInAction checkIn={checkIn} theme={theme} labels={labels} />
        </Box>
      )}
      <Form
        id={isEditing ? 'modify-test-drive-form' : 'schedule-test-drive-form'}
        ref={formRef}
        formFields={formFields}
        onSuccess={handleSuccess}
        onError={handleError}
        onDirtyChange={onDirtyChange}
        disableSubmitUntilDirty={isEditing && !isHostUnsaved}
        isFluid
        locale={locale}
        language={language ?? undefined}
        theme={invertedTheme}
      />
    </Box>
  );
};

export default ScheduleTestDriveForm;
